/**
 * Lateness is told once a day, not as each card goes late.
 *
 * A reminder, an overdue and a critical notice are about things that were
 * already going to be late, and they land at the end of a day when nobody is
 * reading. They are held until the morning the organisation chose (a setting
 * that cascades like the time zone, and is read in the site's zone) and
 * told together, with each card checked again first: one finished or skipped
 * in the meantime is left out, and a card that went from reminder to overdue
 * is told once, as overdue. A card where minutes matter (an access
 * revocation) is not held; see decisions.md, "Lateness is told once a day".
 */

import { addBusinessDays, atTime, dayAt, isBusinessDay, type Calendar } from "./dates.js";
import type { Effect } from "./engine.js";

/** The hour of the morning a site's lateness digest goes unless somebody chose another. */
export const DEFAULT_DIGEST_HOUR = 9;

/** The reasons that are about lateness, from least to most late. */
export const LATENESS: readonly Effect["why"][] = ["reminder", "overdue", "critical"];

export const isLateness = (why: Effect["why"]): boolean => LATENESS.includes(why);

/** Later in `LATENESS` is later; a card is told at the latest it has reached. */
export const latenessRank = (why: string): number => LATENESS.indexOf(why as Effect["why"]);

export const isDigestHour = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 23;

/** "9am", "noon", "5pm", for a screen. */
export function hourText(hour: number): string {
  if (hour === 0) return "midnight";
  if (hour === 12) return "noon";
  return hour < 12 ? `${hour}am` : `${hour - 12}pm`;
}

/**
 * When lateness found at `now` goes out: `hour` o'clock at the site on the
 * next working day that is still ahead, so something that went late on a
 * Friday evening is told on Monday morning and not at the weekend.
 */
export function digestReleaseAt(now: number, hour: number, timeZone: string, calendar: Calendar): number {
  const time = `${String(hour).padStart(2, "0")}:00`;
  const today = dayAt(now, timeZone);
  if (isBusinessDay(today, calendar)) {
    const thisMorning = atTime(today, time, timeZone);
    if (thisMorning > now) return thisMorning;
  }
  return atTime(addBusinessDays(today, 1, calendar), time, timeZone);
}
